'use client';

import type { PortableTextComponents } from '@portabletext/react';
import { PortableText } from '@portabletext/react';
import { cn } from '@/app/utils';
import type { RichTextValue } from '../types';

const portableTextComponents: PortableTextComponents = {
  block: {
    normal: ({ children, value }) => {
      const text = value?.children?.map((c) => ('text' in c ? (c as { text?: string }).text : '')).join('') ?? '';
      if (!text.trim()) return null;
      return <p>{children}</p>;
    },
    h2: ({ children }) => (
      <h2 className="mt-6 mb-3 font-semibold text-2xl text-white leading-[1.2] first:mt-0 sm:text-xl">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="mt-5 mb-2 font-semibold text-white text-xl leading-[1.3] first:mt-0 sm:text-lg">{children}</h3>
    ),
  },
  list: {
    bullet: ({ children }) => <ul className="list-disc space-y-1.5 pl-5">{children}</ul>,
    number: ({ children }) => <ol className="list-decimal space-y-1.5 pl-5">{children}</ol>,
  },
  listItem: {
    bullet: ({ children }) => <li>{children}</li>,
    number: ({ children }) => <li>{children}</li>,
  },
  marks: {
    strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    link: ({ children, value }) => {
      const isExternal = value?.href?.startsWith('http');

      return (
        <a
          className="text-[#0086FF] underline underline-offset-4 transition-colors hover:text-[#3da3ff]"
          href={value?.href}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          target={isExternal ? '_blank' : undefined}
        >
          {children}
        </a>
      );
    },
  },
};

interface PortableTextContentProps {
  className?: string;
  value?: RichTextValue;
}

export function PortableTextContent({ className, value }: PortableTextContentProps) {
  if (!value || value.length === 0) {
    return null;
  }

  return (
    <div className={cn('space-y-3 text-base text-white/55 leading-6 sm:text-sm sm:leading-[22px]', className)}>
      <PortableText components={portableTextComponents} value={value} />
    </div>
  );
}
